import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { LuClipboardList, LuPlus, LuX, LuPackageSearch, LuChevronDown, LuChevronRight, LuList, LuLayoutGrid, LuInfo } from "react-icons/lu";

import RequestsStats from "./requestPageComponents/RequestsStats";
import RequestsFilter from "./requestPageComponents/RequestsFilter";
import RequestsActionBar from "./requestPageComponents/RequestsActionbar";
import RequestFormModal from "./requestPageComponents/RequestsFormModal";
import RequestsLogs from "./requestPageComponents/RequestsLogs";
import RequestStatusSelect from "./requestPageComponents/RequestStatusSelect";
import PurchaseOrder from "./requestPageComponents/PurchaseOrder";
import RequestDetailModal from "./requestPageComponents/RequestDetailModal";

import ConfirmDialog from "../../common/ConfirmDialog";
import Toaster, { notify } from "../../common/Toast";
import TruncatedText from "../../common/TruncatedText";
import Pagination from "../../common/Pagination";
import api from "../../interceptor/Interceptor";
import { SUPPLIER_LIST } from "../../api/ApiEndpoints";

import {
  fetchRequests,
  addRequest,
  editRequest,
  deleteRequests,
  fetchLastPoNum,
  fetchLookups,
} from "../../redux/action/requestsAction/RequestsAction";
import { Field, relativeDate, StatusBadge, SUPPLIER_DOTS, SUPPLIER_THEMES } from "../../utils/Constant";
import Loader from "../../common/Loader";

const hashName = (name = "") => Array.from(name).reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
const dotFor = (name = "") => SUPPLIER_DOTS[hashName(name) % SUPPLIER_DOTS.length];
const themeFor = (name = "") => SUPPLIER_THEMES[hashName(name) % SUPPLIER_THEMES.length];

const InfoButton = ({ onClick }) => (
  <button
    type="button"
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    title="View full details"
    className="flex h-3 w-3 shrink-0 items-center justify-center rounded-full bg-yellow-100 text-yellow-500 transition hover:bg-yellow-200 hover:text-yellow-700"
  >
    <LuInfo size={16} />
  </button>
);

export default function Requests() {
  const dispatch = useDispatch();
  const [params] = useSearchParams();
  const user = useSelector((state) => state.auth.user);
  const { bySupplier, supplierOrder, supplierTotals, approvedBySupplier, pagination, listLoading, addLoading, editLoading, deleteLoading, types, projects, userNames } =
    useSelector((state) => state.request);
  const [filterQuery, setFilterQuery] = useState({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [allSupplierNames, setAllSupplierNames] = useState([]);
  const [selectedRow, setSelectedRow] = useState(new Map());
  const [reloadKey, setReloadKey] = useState(0);

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState("add");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [viewMode, setViewMode] = useState("list");
  const [detailItem, setDetailItem] = useState(null);

  // .................... Expand / Collapse per supplier ...........

  const [collapsedSuppliers, setCollapsedSuppliers] = useState(new Set());

  const toggleSupplier = (supplier) => {
    setCollapsedSuppliers((prev) => {
      const next = new Set(prev);
      if (next.has(supplier)) {
        next.delete(supplier);
      } else {
        next.add(supplier);
      }
      return next;
    });
  };

  // .................... Resolve the active query (URL params win) ...........

  const paramQuery = useMemo(() => {
    if (params.get("user")) return { by: params.get("user") };
    if (params.get("supplier")) return { supplier: params.get("supplier") };
    if (params.get("status")) return { status: params.get("status") };
    return null;
  }, [params]);

  const activeQuery = paramQuery || filterQuery;
  const highlightId = Number(params.get("highlight")) || null;
  const [highlighted, setHighlighted] = useState(highlightId);

  const focusPending = useRef(!!highlightId);
  const skipNextFetch = useRef(false);
  const queryKey = JSON.stringify(activeQuery);
  const lastQueryKey = useRef(queryKey);

  useEffect(() => {
    if (lastQueryKey.current !== queryKey) {
      lastQueryKey.current = queryKey;
      if (page !== 1) {
        setPage(1);
        return;
      }
    }
    if (skipNextFetch.current) {
      skipNextFetch.current = false;
      return;
    }
    const focusId = focusPending.current ? highlightId : null;
    focusPending.current = false;
    dispatch(fetchRequests(activeQuery, page, pageSize, focusId)).then((res) => {
      if (!res.success) {
        notify.error("Failed to fetch requests", res.error);
      } else if (res.pagination.page !== page) {
        skipNextFetch.current = true;
        setPage(res.pagination.page);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, queryKey, page, pageSize, reloadKey]);

  useEffect(() => {
    dispatch(fetchLastPoNum());
    dispatch(fetchLookups());
  }, [dispatch]);

  useEffect(() => {
    api
      .get(SUPPLIER_LIST)
      .then((res) => setAllSupplierNames((res.data.suppliers || []).map((s) => s.name)))
      .catch(() => setAllSupplierNames([]));
  }, [reloadKey]);

  const changePage = (p) => {
    setSelectedRow(new Map());
    setPage(p);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const changePageSize = (size) => {
    setSelectedRow(new Map());
    setPageSize(size);
    setPage(1);
  };

  const refetch = () => setReloadKey((k) => k + 1);

  useEffect(() => {
    if (!highlightId || listLoading) return;
    const el = document.getElementById(`request-${highlightId}`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlighted(highlightId);
    const timer = setTimeout(() => setHighlighted(null), 4000);
    return () => clearTimeout(timer);
  }, [highlightId, listLoading, bySupplier]);

  // .................... Derived data ...........................

  const suppliers = (supplierOrder || Object.keys(bySupplier)).filter((s) => bySupplier[s]?.length > 0);
  const formModalSuppliers = allSupplierNames.length > 0 ? allSupplierNames : Object.keys(bySupplier);
  const allItems = suppliers.flatMap((s) => bySupplier[s]);
  const totalRequests = pagination?.totalRequests ?? allItems.length;
  const hasAnyItems = totalRequests > 0;

  // .................... Selection ...........................

  const selectedCount = selectedRow.size;
  const selectedIds = Array.from(selectedRow.keys());
  const singleSelected =
    selectedCount === 1
      ? { id: selectedIds[0], ...selectedRow.get(selectedIds[0]) }
      : null;

  const handleRowClick = (item) => {
    const updated = new Map(selectedRow);
    if (updated.has(item.id)) {
      updated.delete(item.id);
    } else {
      const { id, ...rest } = item;
      updated.set(id, rest);
    }
    setSelectedRow(updated);
  };

  const handleSelectAll = () => {
    if (selectedRow.size === 0) {
      setSelectedRow(new Map(allItems.map(({ id, ...rest }) => [id, rest])));
    } else {
      setSelectedRow(new Map());
    }
  };

  // .................... Add / Edit ...........................

  const openAddModal = () => {
    setFormMode("add");
    setFormOpen(true);
  };

  const openEditModal = () => {
    if (!singleSelected) return;
    setFormMode("edit");
    setFormOpen(true);
  };

  const handleFormSubmit = async (form) => {
    const res =
      formMode === "edit"
        ? await dispatch(editRequest(singleSelected.id, form, user?.email))
        : await dispatch(addRequest(form, user?.email));

    if (res.success) {
      notify.success(
        formMode === "edit" ? "Successful Update" : "Successful Addition",
        formMode === "edit"
          ? "Item has been successfully updated in requests."
          : "Item has been successfully added to requests.",
      );
      setFormOpen(false);
      setSelectedRow(new Map());
      refetch();
    } else {
      notify.error(
        formMode === "edit" ? "Failed to update request" : "Failed to add request",
        res.error,
      );
    }
  };

  // .................... Delete ...........................

  const handleDelete = async () => {
    const res = await dispatch(deleteRequests(selectedIds));
    if (res.success) {
      notify.success("Successful Deletion", "Item(s) have been successfully deleted from requests.");
      setSelectedRow(new Map());
      setDeleteConfirmOpen(false);
      refetch();
    } else {
      notify.error("Failed to delete request(s)", res.error);
    }
  };

  const getPendingPO = (supplier, items) => {
    const approved =
      approvedBySupplier?.[supplier] ?? items.filter((item) => item.status === "approved");
    const selectedApproved = approved.filter((item) => selectedRow.has(item.id));
    return selectedApproved.length > 0 ? selectedApproved : approved;
  };

  const handlePOGenerated = () => {
    setSelectedRow(new Map());
    refetch();
  };

  return (
    <>
      <div className={`w-full bg-gray-50 ${selectedCount > 0 ? "pb-24" : ""}`}>
        <div className="mx-auto w-full max-w-[1600px] px-2.5 py-3 sm:px-4 sm:py-4 lg:px-6">
          {/* ---------------------------- Header ---------------------------- */}
          <div className="flex flex-col gap-3 border-b border-gray-200 pb-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2.5">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-gray-900 text-white">
                <LuClipboardList size={16} />
              </div>
              <div className="min-w-0">
                <h1 className="text-[15px] font-semibold text-gray-900">Requests</h1>
                <p className="truncate text-[12px] text-gray-500">
                  Track and manage inventory requests by supplier.
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
              <div className="flex flex-wrap items-center gap-2">
                <div className="flex items-center rounded-md border border-gray-200 bg-white p-0.5">
                  <button
                    type="button"
                    onClick={() => setViewMode("list")}
                    title="List view"
                    className={`flex h-7 w-7 items-center justify-center rounded transition ${
                      viewMode === "list" ? "bg-gray-900 text-white" : "text-gray-400 hover:text-gray-600"
                    }`}
                  >
                    <LuList size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode("card")}
                    title="Card view"
                    className={`flex h-7 w-7 items-center justify-center rounded transition ${
                      viewMode === "card" ? "bg-gray-900 text-white" : "text-gray-400 hover:text-gray-600"
                    }`}
                  >
                    <LuLayoutGrid size={14} />
                  </button>
                </div>
                
                <RequestsFilter
                  query={paramQuery ? {} : filterQuery}
                  onApply={setFilterQuery}
                  onClear={() => setFilterQuery({})}
                  suppliers={formModalSuppliers}
                  types={types}
                  projects={projects}
                  userNames={userNames}
                  disabled={(!hasAnyItems && Object.keys(filterQuery).length === 0) || !!paramQuery}
                />
                {paramQuery && (
                  <a
                    href="/requests"
                    className="flex h-8 items-center gap-1 rounded-md border border-amber-300 bg-amber-50 px-2.5 text-[12px] font-medium text-amber-700 transition hover:bg-amber-100"
                  >
                    <LuX size={12} />
                    Clear URL filter
                  </a>
                )}
              </div>
              <button
                onClick={openAddModal}
                className="flex h-8 w-full items-center justify-center gap-1.5 rounded-md bg-brand-gradient px-3 text-[12.5px] font-semibold text-white shadow-sm transition hover:opacity-90 sm:w-auto"
              >
                <LuPlus size={13} />
                Add Request
              </button>
            </div>
          </div>

          <RequestsStats numRequests={totalRequests} />

          {listLoading && (
           <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-gray-700" />
              <Loader fullScreen text="Loading requests…" />
            </div>
          )}

          {!listLoading && suppliers.length === 0 && (
            <div className="flex flex-col items-center justify-center gap-1.5 rounded-lg border border-dashed border-gray-200 bg-white py-16 text-center">
              <LuPackageSearch size={24} className="mb-1 text-gray-300" strokeWidth={1.5} />
              <p className="text-[12.5px] font-medium text-gray-600">No requests found</p>
              <p className="max-w-xs text-[12px] text-gray-400">
                {Object.keys(activeQuery).length > 0
                  ? "Nothing matches the applied filters."
                  : "Requests will be grouped here by supplier once added."}
              </p>
            </div>
          )}

          {/* ------------------------- Supplier groups ------------------------ */}
          {!listLoading &&
            suppliers.map((supplier) => {
              const items = bySupplier[supplier];
              const isCollapsed = collapsedSuppliers.has(supplier);
              const theme = themeFor(supplier);
              return (
                <div
                  key={supplier}
                  className={`group mb-5 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm transition-all duration-200 hover:shadow-lg ${theme.hoverBorder} ${theme.hoverShadow}`}
                >
                  {/* Supplier header */}
                  <div
                    className={`flex cursor-pointer flex-col gap-2 border-gray-200 bg-gray-50/70 px-3.5 py-2.5 transition-colors hover:bg-gray-100/80 sm:flex-row sm:items-center sm:justify-between ${
                      !isCollapsed ? "border-b" : ""
                    }`}
                    onClick={() => toggleSupplier(supplier)}
                    role="button"
                    tabIndex={0}
                    aria-expanded={!isCollapsed}
                    aria-label={isCollapsed ? `Expand ${supplier}` : `Collapse ${supplier}`}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggleSupplier(supplier);
                      }
                    }}
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <span
                        aria-hidden="true"
                        className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-gray-400 transition-transform duration-200"
                      >
                        {isCollapsed ? <LuChevronRight size={14} /> : <LuChevronDown size={14} />}
                      </span>
                      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${dotFor(supplier)}`} />
                      <TruncatedText text={supplier} className="text-[13px] font-semibold text-gray-900" />
                      {(() => {
                        const total = supplierTotals?.[supplier] ?? items.length;
                        return total > items.length ? (
                          <span
                            className="shrink-0 rounded-full bg-amber-50 px-1.5 py-0.5 text-[10.5px] font-medium text-amber-700"
                            title="This supplier's requests continue on another page"
                          >
                            {items.length} of {total} items
                          </span>
                        ) : (
                          <span className="shrink-0 text-[11px] text-gray-400">
                            {items.length} {items.length === 1 ? "item" : "items"}
                          </span>
                        );
                      })()}
                    </div>
                    {items.length > 0 && (
                      <div className="w-full shrink-0 sm:w-52" onClick={(e) => e.stopPropagation()}>
                        <PurchaseOrder
                          supplier={supplier}
                          items={getPendingPO(supplier, items)}
                          cellPhoneView={false}
                          onGenerated={handlePOGenerated}
                        />
                      </div>
                    )}
                  </div>

                  {!isCollapsed && viewMode === "list" && (
                    /* ---------------------- List view (rows) ---------------------- */
                    <div className="divide-y divide-gray-100">
                      {items.map((item) => {
                        const isSelected = selectedRow.has(item.id);
                        const total = (item.numUnits * item.unitPrice).toFixed(2);
                        return (
                          <div
                            key={item.id}
                            id={`request-${item.id}`}
                            onClick={() => handleRowClick(item)}
                            className={`min-w-0 w-full cursor-pointer border-l-[3px] p-3.5 transition-colors sm:p-4 ${
                              isSelected
                                ? "border-l-indigo-400 bg-indigo-50/50"
                                : highlighted === item.id
                                ? "border-l-amber-400 bg-amber-50"
                                : "border-l-transparent hover:bg-gray-50/70"
                            }`}
                          >
                            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:gap-5">
                              {/* Title */}
                              <div className="flex min-w-0 items-start gap-2.5 lg:w-60 lg:shrink-0">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => handleRowClick(item)}
                                  onClick={(e) => e.stopPropagation()}
                                  className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-brand-600 focus:ring-1 focus:ring-brand-500/40"
                                />
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2">
                                    <TruncatedText text={item.item} className="text-[14.5px] font-bold leading-tight text-gray-900" />
                                    <InfoButton onClick={() => setDetailItem(item)} />
                                    <StatusBadge status={item.status} />
                                  </div>
                                  <p className="mt-1 truncate text-[11.5px] text-gray-400">
                                    {relativeDate(item.date)}
                                    {item.poNum ? ` · PO #${item.poNum}` : ""}
                                  </p>
                                </div>
                              </div>

                              {/* Data grid */}
                              <div className="grid grid-cols-2 gap-x-3 gap-y-2.5 border-t border-gray-100 pt-3 sm:grid-cols-3 md:grid-cols-6 lg:flex-1 lg:border-t-0 lg:border-l lg:pt-0 lg:pl-5">
                                <Field label="Type">{item.type}</Field>
                                <Field label="Catalog">{item.catalog}</Field>
                                <Field label="Mfr">{item.manufacturer}</Field>
                                <Field label="Qty" className="font-mono">{item.unitSize}×{item.numUnits}</Field>
                                <Field label="Unit" className="font-mono">₹{item.unitPrice}</Field>
                                <Field label="Total" className="font-mono text-brand-700">₹{total}</Field>
                              </div>

                              {/* Requester / project */}
                              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-gray-100 pt-3 lg:w-36 lg:shrink-0 lg:flex-col lg:items-start lg:border-t-0 lg:border-l lg:pt-0 lg:pl-5">
                                <Field label="By">{item.by}</Field>
                                {item.project && <Field label="Project">{item.project}</Field>}
                              </div>

                              {/* Actions */}
                              <div
                                className="flex items-center justify-between gap-3 border-t border-gray-100 pt-3 lg:w-auto lg:shrink-0 lg:justify-end lg:border-t-0 lg:border-l lg:pt-0 lg:pl-5"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <div className="shrink-0 whitespace-nowrap">
                                  <RequestStatusSelect requestId={item.id} status={item.status} onChanged={refetch} />
                                </div>
                                  <RequestsLogs requestId={item.id} />
                              </div>
                            </div>

                            {item.notes && (
                              <p className="mt-2.5 truncate text-[11.5px] italic text-gray-400">{item.notes}</p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {!isCollapsed && viewMode === "card" && (
                    /* ---------------------- Card view (grid) ---------------------- */
                    <div className="grid grid-cols-1 gap-3 p-3 sm:grid-cols-2 xl:grid-cols-3">
                      {items.map((item) => {
                        const isSelected = selectedRow.has(item.id);
                        const total = (item.numUnits * item.unitPrice).toFixed(2);
                        return (
                          <div
                            key={item.id}
                            id={`request-${item.id}`}
                            onClick={() => handleRowClick(item)}
                            className={`flex min-w-0 cursor-pointer flex-col rounded-lg border p-3 transition-colors ${
                              isSelected
                                ? "border-indigo-300 bg-indigo-50/50 ring-1 ring-indigo-200"
                                : highlighted === item.id
                                ? "border-amber-300 bg-amber-50 ring-1 ring-amber-200"
                                : "border-gray-200 hover:border-gray-300 hover:bg-gray-50/70"
                            }`}
                          >
                            <div className="flex items-start gap-2">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleRowClick(item)}
                                onClick={(e) => e.stopPropagation()}
                                className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-brand-600 focus:ring-1 focus:ring-brand-500/40"
                              />
                              <div className="min-w-0 flex-1">
                                <div className="flex items-start justify-between gap-2">
                                  <div className="flex items-center gap-2">
                                  <TruncatedText text={item.item} className="text-[13.5px] font-bold leading-tight text-gray-900" />
                                   <InfoButton onClick={() => setDetailItem(item)} />
                                    </div>
                                  <StatusBadge status={item.status} />
                                </div>
                                <p className="mt-0.5 truncate text-[11px] text-gray-400">
                                  {relativeDate(item.date)}
                                  {item.poNum ? ` · PO #${item.poNum}` : ""}
                                </p>
                              </div>
                            </div>

                            <div className="mt-2.5 grid grid-cols-2 gap-x-3 gap-y-2 border-t border-gray-100 pt-2.5">
                              <Field label="Type">{item.type}</Field>
                              <Field label="Catalog">{item.catalog}</Field>
                              <Field label="Qty" className="font-mono">{item.unitSize}×{item.numUnits}</Field>
                              <Field label="Total" className="font-mono text-brand-700">₹{total}</Field>
                            </div>

                            <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-gray-100 pt-2.5">
                              <Field label="By">{item.by}</Field>
                              {item.project && <Field label="Project">{item.project}</Field>}
                            </div>

                            <div
                              className="mt-2.5 flex items-center justify-between gap-2 border-t border-gray-100 pt-2.5"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="min-w-0 flex-1">
                                <RequestStatusSelect requestId={item.id} status={item.status} onChanged={refetch} />
                              </div>
                                <RequestsLogs requestId={item.id} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}

          {!listLoading && (
            <Pagination
              page={pagination?.page || page}
              totalPages={pagination?.totalPages || 1}
              totalItems={pagination?.totalItems || 0}
              pageSize={pageSize}
              onPageChange={changePage}
              onPageSizeChange={changePageSize}
              pageSizeOptions={[10, 20, 50]}
              itemLabel={totalRequests === 1 ? "request" : "requests"}
              extra={`${pagination?.totalSuppliers ?? 0} supplier${pagination?.totalSuppliers === 1 ? "" : "s"}`}
              loading={listLoading}
            />
          )}
        </div>

        <RequestsActionBar
          selectedCount={selectedCount}
          canEdit={selectedCount === 1}
          canDelete={selectedCount > 0}
          onEdit={openEditModal}
          onDelete={() => setDeleteConfirmOpen(true)}
          onClear={() => setSelectedRow(new Map())}
        />
      </div>

      <RequestFormModal
        open={formOpen}
        mode={formMode}
        initialValue={formMode === "edit" ? singleSelected : null}
        suppliers={formModalSuppliers}
        types={types}
        onClose={() => setFormOpen(false)}
        onSubmit={handleFormSubmit}
        loading={formMode === "edit" ? editLoading : addLoading}
      />

      <ConfirmDialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
        title={selectedCount === 1 ? "Delete request?" : "Delete requests?"}
        description={`This will permanently delete ${selectedCount} request${
          selectedCount > 1 ? "s" : ""
        }. This action cannot be undone.`}
        confirmLabel="Delete"
        tone="danger"
        loading={deleteLoading}
      />

      <RequestDetailModal item={detailItem} onClose={() => setDetailItem(null)} />

      <Toaster />
    </>
  );
}